import { useState } from 'react';
import { Button } from '@/deploy/components/ui/Button';
import { Toggle } from '@/deploy/components/ui/Toggle';
import { Icon } from '@/deploy/components/ui/Icon';
import type { AccessRule, AccessRulePayload } from '@/deploy/types/network';
import styles from './Network.module.scss';

type Scope = 'domain' | 'prefix' | 'exact';

interface UserDraft {
  username: string;
  password: string;
  /** Utilisateur déjà enregistré : mot de passe vide = inchangé. */
  existing: boolean;
}

interface KeyDraft {
  id: string | null;
  name: string;
  prefix: string | null;
  /** Nouvelle clé, affichée tant que le formulaire est ouvert puis plus jamais. */
  secret: string | null;
}

export interface RuleDraft {
  uid: string;
  id: string | null;
  scope: Scope;
  path: string;
  ips: string[];
  users: UserDraft[];
  keys: KeyDraft[];
  acceptXApiKey: boolean;
}

const IP_OR_CIDR = /^(\d{1,3}(\.\d{1,3}){3}(\/\d{1,2})?|[0-9a-fA-F:.]*:[0-9a-fA-F:.]*(\/\d{1,3})?)$/;

let uidCounter = 0;
const uid = () => `rule-${++uidCounter}`;

/** 30 octets aléatoires en base64url : 40 caractères, accepté tel quel par ssl-manager et nginx. */
function generateKey(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(30));
  return btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function toDrafts(rules: AccessRule[] | undefined): RuleDraft[] {
  return (rules ?? []).map((r) => ({
    uid: uid(),
    id: r.id,
    scope: r.path === null ? 'domain' : r.exact ? 'exact' : 'prefix',
    path: r.path ?? '',
    ips: r.allowIps,
    users: r.basicUsers.map((u) => ({ username: u, password: '', existing: true })),
    keys: r.bearerKeys.map((k) => ({ id: k.id, name: k.name, prefix: k.prefix, secret: null })),
    acceptXApiKey: r.acceptXApiKey,
  }));
}

export function toPayload(drafts: RuleDraft[]): AccessRulePayload[] {
  return drafts.map((d) => ({
    id: d.id,
    path: d.scope === 'domain' ? null : d.path.trim(),
    exact: d.scope === 'exact',
    allowIps: d.ips,
    basicUsers: d.users.map((u) => ({ username: u.username.trim(), password: u.password || null })),
    bearerKeys: d.keys.map((k) => ({ id: k.id, name: k.name.trim(), secret: k.secret })),
    acceptXApiKey: d.keys.length > 0 && d.acceptXApiKey,
  }));
}

/** Résumé d'une règle pour le badge de la liste des domaines. */
export function describeRule(r: AccessRule): string {
  const parts = [
    r.allowIps.length > 0 && `IP (${r.allowIps.length})`,
    r.basicUsers.length > 0 && `Basic (${r.basicUsers.length})`,
    r.bearerKeys.length > 0 && `Bearer (${r.bearerKeys.length})${r.acceptXApiKey ? ' + X-API-Key' : ''}`,
  ].filter(Boolean);
  const where = r.path === null ? 'Tout le domaine' : `${r.exact ? '= ' : ''}${r.path}`;
  return `${where} : ${parts.join(', ')}`;
}

interface AccessRulesEditorProps {
  rules: RuleDraft[];
  onChange: (rules: RuleDraft[]) => void;
}

/**
 * Règles d'accès d'un vhost : tout le domaine ou un chemin, chacune avec une whitelist IP,
 * des utilisateurs Basic et des clés Bearer, tous optionnels.
 */
export function AccessRulesEditor({ rules, onChange }: AccessRulesEditorProps) {
  const update = (index: number, patch: Partial<RuleDraft>) =>
    onChange(rules.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  const hasDomainRule = rules.some((r) => r.scope === 'domain');

  const add = () => onChange([...rules, {
    uid: uid(),
    id: null,
    scope: hasDomainRule ? 'prefix' : 'domain',
    path: hasDomainRule ? '/' : '',
    ips: [],
    users: [],
    keys: [],
    acceptXApiKey: false,
  }]);

  return (
    <div className={styles.field}>
      <span>Sécurité</span>
      {rules.length === 0 && (
        <span className={styles.hint}>Aucune restriction : le domaine est accessible à tous.</span>
      )}
      {rules.map((rule, i) => (
        <RuleCard key={rule.uid} rule={rule} domainTaken={hasDomainRule && rule.scope !== 'domain'}
          onChange={(patch) => update(i, patch)} onRemove={() => onChange(rules.filter((_, j) => j !== i))} />
      ))}
      <div>
        <Button variant="ghost" size="sm" leftIcon={<Icon name="plus" size={12} />} onClick={add} disabled={rules.length >= 20}>
          Ajouter une règle
        </Button>
      </div>
      {rules.some((r) => r.scope !== 'domain') && (
        <span className={styles.hint}>
          Une règle sur un chemin remplace celle du domaine sur ce chemin (elle ne s'y ajoute pas), comme une location nginx.
        </span>
      )}
    </div>
  );
}

function RuleCard({ rule, domainTaken, onChange, onRemove }: {
  rule: RuleDraft;
  domainTaken: boolean;
  onChange: (patch: Partial<RuleDraft>) => void;
  onRemove: () => void;
}) {
  const [ipDraft, setIpDraft] = useState('');
  const [ipError, setIpError] = useState<string | null>(null);
  const empty = rule.ips.length === 0 && rule.users.length === 0 && rule.keys.length === 0;

  const addIp = () => {
    const values = ipDraft.split(/[\s,]+/).map((v) => v.trim()).filter(Boolean);
    if (values.length === 0) return;
    const invalid = values.filter((v) => !IP_OR_CIDR.test(v));
    if (invalid.length > 0) {
      setIpError(`IP ou CIDR invalide : ${invalid.join(', ')}`);
      return;
    }
    setIpError(null);
    onChange({ ips: [...rule.ips, ...values.filter((v) => !rule.ips.includes(v))] });
    setIpDraft('');
  };

  const setUser = (i: number, patch: Partial<UserDraft>) =>
    onChange({ users: rule.users.map((u, j) => (j === i ? { ...u, ...patch } : u)) });
  const setKey = (i: number, patch: Partial<KeyDraft>) =>
    onChange({ keys: rule.keys.map((k, j) => (j === i ? { ...k, ...patch } : k)) });

  return (
    <div className={styles.step}>
      <div className={styles.inlineItem}>
        <select value={rule.scope} style={{ flex: '0 0 170px' }}
          onChange={(e) => onChange({ scope: e.target.value as Scope, path: e.target.value === 'domain' ? '' : rule.path || '/' })}>
          <option value="domain" disabled={domainTaken}>Tout le domaine</option>
          <option value="prefix">Chemin (préfixe)</option>
          <option value="exact">Chemin exact</option>
        </select>
        {rule.scope !== 'domain' && (
          <input value={rule.path} onChange={(e) => onChange({ path: e.target.value })}
            placeholder={rule.scope === 'exact' ? '/health' : '/admin/'} className={styles.mono} />
        )}
        {rule.scope === 'domain' && <span className={styles.spacer} />}
        <Button variant="ghost" size="sm" leftIcon={<Icon name="x" size={12} />} onClick={onRemove} aria-label="Retirer la règle" />
      </div>

      <div className={styles.field}>
        <span>IP autorisées</span>
        <div className={styles.chips}>
          {rule.ips.map((ip) => (
            <span key={ip} className={styles.chip}>
              {ip}
              <button type="button" aria-label={`Retirer ${ip}`} onClick={() => onChange({ ips: rule.ips.filter((x) => x !== ip) })}>
                <Icon name="x" size={10} />
              </button>
            </span>
          ))}
          <input value={ipDraft} placeholder="1.2.3.4, 10.0.0.0/8, 2001:db8::/32"
            onChange={(e) => setIpDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addIp(); } }}
            onBlur={addIp} />
        </div>
        {ipError
          ? <span className={styles.error}>{ipError}</span>
          : <span className={styles.hint}>Vide : pas de filtrage IP. Sinon une IP hors liste reçoit un 403, même avec des identifiants valides.</span>}
      </div>

      <div className={styles.field}>
        <span>Utilisateurs (Basic auth)</span>
        {rule.users.map((u, i) => (
          <div key={i} className={styles.inlineItem}>
            <input value={u.username} onChange={(e) => setUser(i, { username: e.target.value })} placeholder="utilisateur"
              disabled={u.existing} autoComplete="off" />
            <input type="password" value={u.password} onChange={(e) => setUser(i, { password: e.target.value })}
              placeholder={u.existing ? 'inchangé' : 'mot de passe (8 caractères min.)'} autoComplete="new-password" />
            <Button variant="ghost" size="sm" leftIcon={<Icon name="x" size={12} />} aria-label="Retirer"
              onClick={() => onChange({ users: rule.users.filter((_, j) => j !== i) })} />
          </div>
        ))}
        <div>
          <Button variant="ghost" size="sm" leftIcon={<Icon name="plus" size={12} />}
            onClick={() => onChange({ users: [...rule.users, { username: '', password: '', existing: false }] })}>
            Ajouter un utilisateur
          </Button>
        </div>
      </div>

      <div className={styles.field}>
        <span>Clés API (Bearer)</span>
        {rule.keys.map((k, i) => (
          <div key={k.id ?? `new-${i}`} className={styles.field}>
            <div className={styles.inlineItem}>
              <input value={k.name} onChange={(e) => setKey(i, { name: e.target.value })} placeholder="nom (ex : ci, monitoring)" />
              {k.prefix && <code className={styles.mono}>{k.prefix}…</code>}
              <Button variant="ghost" size="sm" leftIcon={<Icon name="x" size={12} />} aria-label="Révoquer"
                onClick={() => onChange({ keys: rule.keys.filter((_, j) => j !== i) })} />
            </div>
            {k.secret && (
              <>
                <div className={styles.keyBox}>
                  <code title={k.secret}>{k.secret}</code>
                  <Button variant="ghost" size="sm" onClick={() => void navigator.clipboard.writeText(k.secret!)}>Copier</Button>
                </div>
                <span className={styles.hint}>Copie-la maintenant : elle ne sera plus jamais affichée après l'enregistrement.</span>
              </>
            )}
          </div>
        ))}
        <div>
          <Button variant="ghost" size="sm" leftIcon={<Icon name="plus" size={12} />}
            onClick={() => onChange({ keys: [...rule.keys, { id: null, name: '', prefix: null, secret: generateKey() }] })}>
            Générer une clé
          </Button>
        </div>
        {rule.keys.length > 0 && (
          <Toggle checked={rule.acceptXApiKey} onChange={(v) => onChange({ acceptXApiKey: v })} label="Accepter aussi X-API-Key"
            description="En plus de Authorization: Bearer <clé>, la clé est acceptée dans l'en-tête X-API-Key." />
        )}
        {rule.users.length > 0 && rule.keys.length > 0 && (
          <span className={styles.hint}>Basic et Bearer ensemble : l'un ou l'autre suffit.</span>
        )}
      </div>

      {empty && <span className={styles.error}>Cette règle ne protège rien : ajoute une IP, un utilisateur ou une clé.</span>}
    </div>
  );
}
